<script setup lang="ts">
import {
  type DiscreteNumericValueDto,
  instanceOfDiscreteNumericListDto,
  instanceOfDiscreteNumericValueDto,
  instanceOfEnumerationValueListDto,
  instanceOfNumericListDto,
  instanceOfTextValueDto,
  instanceOfTextValueListDto,
  type ISingleSystemSetupParameter,
  type NumericListDto,
  TaskTypeDescription,
} from '@/api/generator';
import PtwDeleteButton from '@/components/buttons/PtwDeleteButton.vue';
import VeeDiscreteNumericList from '@/components/forms/dataTypes/VeeDiscreteNumericList.vue';
import VeeDiscreteNumericValue from '@/components/forms/dataTypes/VeeDiscreteNumericValue.vue';
import VeeEnumerationValueList from '@/components/forms/dataTypes/VeeEnumerationValueList.vue';
import VeeNumericList from '@/components/forms/dataTypes/VeeNumericList.vue';
import VeeTextValue from '@/components/forms/dataTypes/VeeTextValue.vue';
import VeeTextValueList from '@/components/forms/dataTypes/VeeTextValueList.vue';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import { toDisplayValue } from '@/utils/numberUtils';
import { I18nKey } from '@/utils/typings.ts';
import SetupParameterLabel from '@/views/tasks/components/SetupParameterLabel.vue';
import { useFieldArray, useFieldValue, useSetFieldValue } from 'vee-validate';

type SingleSetupParameterProps = {
  name: string;
};
const props = defineProps<SingleSetupParameterProps>();
const mode = useFormMode();
const taskType = useFieldValue<TaskTypeDescription>('taskType');

const parameter = useFieldValue<ISingleSystemSetupParameter>(props.name);
const setParameterEnabledState = useSetFieldValue<boolean>(`${props.name}.isEnabled`);

const { remove: removeFieldValue, fields: fieldValues } = useFieldArray(`${props.name}.values`);

function formatNumericValue(
  value: number,
  param: NumericListDto | DiscreteNumericValueDto,
): string {
  return toDisplayValue(value, {
    fractionDigits: param.decimalPlaces || 0,
    unitAbbreviation: param.unitAbbreviation,
    valueType: param.valueType,
  });
}

const cardBinHandler = () => {
  setParameterEnabledState(false);
  for (let i = fieldValues.value.length - 1; i >= 0; i--) {
    removeFieldValue(i);
  }
};
</script>
<template>
  <v-card v-if="parameter && parameter.isEnabled" border class="single-setup-parameter">
    <v-card-item class="pa-0">
      <v-card-title class="parameter-title">
        <div class="text-truncate">
          <SetupParameterLabel :parameter="parameter" />
        </div>
        <VSpacer />
        <PtwDeleteButton
          v-if="mode !== 'details' && taskType === 'ManualTask'"
          @delete="cardBinHandler"
        />
      </v-card-title>
    </v-card-item>
    <v-card-text>
      <VeeDiscreteNumericList
        v-if="instanceOfDiscreteNumericListDto(parameter)"
        :name="name"
        :mode="mode"
      />

      <template v-else-if="instanceOfNumericListDto(parameter)">
        <VeeNumericList
          v-if="mode !== 'details'"
          :name="name"
          :mode="mode"
          :deleteTooltipText="`${$t('Remove')} ${$t(parameter.name as I18nKey)}`"
          :addTooltipText="`${$t('Add')} ${$t(parameter.name as I18nKey)}`"
        />

        <div v-else>
          <v-chip v-for="(value, index) in parameter.values" :key="index" class="mr-1 mb-2">
            {{ formatNumericValue(value, parameter) }}
          </v-chip>
        </div>
      </template>

      <template v-else-if="instanceOfTextValueListDto(parameter)">
        <VeeTextValueList
          v-if="mode !== 'details'"
          :name="name"
          :mode="mode"
          :deleteTooltipText="`${$t('Remove')} ${$t(parameter.name as I18nKey)}`"
          :addTooltipText="`${$t('Add')} ${$t(parameter.name as I18nKey)}`"
        />

        <div v-else>
          <v-chip v-for="(value, index) in parameter.values" :key="index" class="mr-1 mb-2">
            {{ value }}
          </v-chip>
        </div>
      </template>

      <VeeEnumerationValueList
        v-else-if="instanceOfEnumerationValueListDto(parameter)"
        class="mt-2"
        :name="name"
        :mode="mode"
      />

      <template v-else-if="instanceOfDiscreteNumericValueDto(parameter)">
        <VeeDiscreteNumericValue
          v-if="mode !== 'details'"
          class="mt-2"
          :name="name"
          :mode="
            parameter.availableValues?.length && parameter.availableValues.length > 1
              ? mode
              : 'details'
          "
        />

        <div v-else-if="parameter.value !== undefined && parameter.value !== null" class="mt-2">
          <v-chip class="mr-1 mb-2">
            {{ formatNumericValue(parameter.value, parameter) }}
          </v-chip>
        </div>
      </template>

      <VeeTextValue
        v-if="instanceOfTextValueDto(parameter)"
        class="mt-2"
        :name="name"
        :mode="mode"
        :placeholder="
          parameter.name === 'TextNameLabelEnum.IndexingPoint'
            ? $t('PlaceholderCouchIndexingPoint')
            : undefined
        "
      />
    </v-card-text>
  </v-card>
</template>
